import { Link } from "react-router-dom";
import { Scale } from "lucide-react";
import PublicFooter from "@/components/PublicFooter";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-brand-link">
            <Scale className="w-5 h-5 text-[#0B1B3D]" strokeWidth={1.75} />
            <span className="font-serif text-xl font-medium">FOIA Assist</span>
          </Link>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Legal</div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="privacy-page">
        <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">Privacy Policy</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight font-medium mb-4">Privacy Policy</h1>
        <div className="fa-badge fa-badge-warning mb-6" data-testid="privacy-template-badge">
          Template — not legal advice. Please have your counsel review before public launch.
        </div>
        <p className="text-slate-600 leading-relaxed mb-8">
          Last updated: {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <section className="space-y-6 text-slate-700 leading-relaxed">
          <div>
            <h2 className="font-serif text-2xl mb-2">1. Scope</h2>
            <p>This Privacy Policy explains what personal information FOIA Assist collects, how we use it, and the choices available to you. It applies to visitors of our website and to authorized users of the FOIA Assist software service.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">2. Information We Collect</h2>
            <p><span className="font-medium">Account information</span> — name, work email, agency name, and password hash you provide when creating an account.</p>
            <p><span className="font-medium">Assessment inputs</span> — answers you submit to the public FOIA Risk Assessment, so we can tailor follow-up and product recommendations.</p>
            <p><span className="font-medium">Customer Data</span> — documents, case notes, and audit entries you or your agency add to the Service.</p>
            <p><span className="font-medium">Usage data</span> — server logs, IP addresses, browser type, and pages visited, used to secure and improve the Service.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">3. How We Use Information</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li>To provide, secure, and support the Service.</li>
              <li>To send transactional messages (welcome, temp password, PO confirmation).</li>
              <li>To communicate with prospective and existing customers about the Service.</li>
              <li>To improve product features, subject to the AI processing controls described below.</li>
            </ul>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">4. AI Processing</h2>
            <p>Documents you upload for AI-assisted redaction or summarization are sent to our AI service provider (Anthropic Claude) solely to generate the output requested by you. We do not permit the AI provider to use Customer Data to train foundation models. AI outputs are always subject to human review before any release.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">5. Sharing</h2>
            <p>We share information only with (a) service providers acting on our behalf under written contract (hosting, email delivery, payments, AI processing), (b) authorized users at your agency, and (c) as required by law. We do not sell personal information.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">6. Data Retention</h2>
            <p>We retain Customer Data for the life of your account plus a reasonable export window after termination, then delete or de-identify it in accordance with our internal schedule. You may request earlier deletion by contacting us.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">7. Security</h2>
            <p>We use encryption in transit, access controls, and audit logging to protect the Service. No system is perfectly secure; you are responsible for keeping account credentials confidential and for reporting suspected unauthorized access.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">8. Cookies</h2>
            <p>We use a small number of strictly necessary cookies to keep you signed in. We do not currently use advertising or cross-site tracking cookies.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">9. Your Choices</h2>
            <p>You can update or delete account information from within the Service, or by writing to the administrator email in your onboarding materials. You can unsubscribe from non-transactional email at any time.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">10. Children</h2>
            <p>The Service is intended for use by adult professionals working for public agencies. We do not knowingly collect information from children under 13.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">11. Changes</h2>
            <p>We may update this Privacy Policy. If we make material changes, we will provide notice through the Service or by email prior to the change taking effect.</p>
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2">12. Contact</h2>
            <p>Privacy questions may be sent to the administrator email listed in your onboarding materials.</p>
          </div>
        </section>

        <div className="mt-10 pt-6 border-t border-slate-200 text-sm text-slate-500">
          <Link to="/terms" className="underline decoration-[#C5A059] underline-offset-2" data-testid="privacy-to-terms-link">Read our Terms of Service →</Link>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
